import { DiscoverGameResponse } from "libs/apiClient";
import { Tabs } from "features/discover/Tabs";
import { useState } from "react";
import { formatDateTime } from "features/discover/functions";
import { Link } from "react-router";
import "style/gameAnalysisDialog.css";
import client from "libs/client";
import { useToast } from "ui-kit";
import { AnalysisType } from "../../../../discover/app/features/games/enums";
import { DiscoverGameAnalysisProvider } from "features/datagrid-configs/discoverGames";
import { _, _any } from "i18n";
import { getTrustedVeoLink } from "common/src/veo-link";

const TEAM_TAGGING = {
	Home: "home",
	Away: "away",
	Both: "both",
} as const;
type ProcessingLevel = "full" | "limited";
type TeamTagged = typeof TEAM_TAGGING[keyof typeof TEAM_TAGGING];
const analysisProviders = [
	DiscoverGameAnalysisProvider.Sportlogiq,
	DiscoverGameAnalysisProvider.Korastats,
	DiscoverGameAnalysisProvider.Ubitrack,
	DiscoverGameAnalysisProvider.Bepro,
];
const providers = ["SPORTLOGIQ", "KORASTATS", "UBITRACK", "BEPRO"];

export function DiscoverGameAnalysisDialog({
	gameData,
	invalidationQueryKey,
}: {
	// Minimal shape shared by list rows and the V2 game detail payload.
	gameData: Pick<DiscoverGameResponse, "id" | "isDiscover" | "gameAnalysis" | "veoLink">;
	invalidationQueryKey: string | string[];
}) {
	const { addSuccessToast, addErrorToast } = useToast();
	const hasTrustedVeoLink = getTrustedVeoLink(gameData.veoLink) !== null;
	const [requestSportlogiqWithPhysical, setRequestSportlogiqWithPhysical] = useState<boolean>(false);
	const [priority, setPriority] = useState<number>(gameData.isDiscover ? AnalysisType.DEFAULT_PRIORITY_DISCOVER : AnalysisType.DEFAULT_PRIORITY_ELITE);
	const [processingLevel, setProcessingLevel] = useState<ProcessingLevel>("full");
	const [isHomeTeamTagged, setIsHomeTeamTagged] = useState<boolean>(true);
	const [isAwayTeamTagged, setIsAwayTeamTagged] = useState<boolean>(true);
	const [hasProgress, setHasProgress] = useState<boolean>(false);

	const actualAnalysis = gameData.gameAnalysis.find((ga) => ga.isInProgress || ga.status === "Done" || ga.status === null);
	const canRequestAnalysis = actualAnalysis === undefined;
	const [activeProviderTab, setActiveProviderTab] = useState<string | null>(actualAnalysis?.provider || null);
	const visibleProviderAnalysis = gameData.gameAnalysis.find((ga) => ga.provider === activeProviderTab) || null;
	const isSportlogiqRequest = activeProviderTab === DiscoverGameAnalysisProvider.Sportlogiq;
	const isSportlogiqDiscoverRequest = gameData.isDiscover && isSportlogiqRequest;
	const canChooseTeamTagging = isSportlogiqDiscoverRequest;
	const isTeamTaggingValid = isHomeTeamTagged || isAwayTeamTagged;
	const invalidationQueryKeys = Array.isArray(invalidationQueryKey) ? invalidationQueryKey : [invalidationQueryKey];
	const tabs = providers.map((p) => {
		const analysis = gameData.gameAnalysis.find((ga) => ga.provider === p);
		return {
			label: <div>
				<div>{p}</div>
				{analysis ? <div>{analysis?.status || "none"}</div> : ""}
			</div>,
			key: p,
		}
	});

	const rejectGameAnalysisMutation = client.rejectGameAnalysis.useMutation({
		onSuccess: () => {
			addSuccessToast("Game analysis rejected")
			client.queryClient.invalidateQueries({
				predicate: (query) => invalidationQueryKeys.some((key) => query.queryKey.includes(key)),
			});
			setHasProgress(false);
		},
		onError: (error) => {
			addErrorToast(error.message);
			setHasProgress(false);
		}
	});

	const handleRejectGameAnalysis = (id: number) => {
		if (!confirm("Are you sure to reject analysis?")) {
			return;
		}
		setHasProgress(true);
		rejectGameAnalysisMutation.mutate({ id })
	}

	const createGameAnalysisMutation = client.createGameAnalysis.useMutation({
		onSuccess: () => {
			addSuccessToast("Game analysis created")
			client.queryClient.invalidateQueries({
				predicate: (query) => invalidationQueryKeys.some((key) => query.queryKey.includes(key)),
			});
			setHasProgress(false);
		},
		onError: (error) => {
			addErrorToast(error.message);
			setHasProgress(false);
		}
	});

	const getTeamTagging = (): TeamTagged | undefined => {
		if (!canChooseTeamTagging) {
			return undefined;
		}

		if (isHomeTeamTagged && isAwayTeamTagged) {
			return TEAM_TAGGING.Both;
		}

		if (isHomeTeamTagged) {
			return TEAM_TAGGING.Home;
		}

		if (isAwayTeamTagged) {
			return TEAM_TAGGING.Away;
		}

		return undefined;
	};

	const handleCreateGameAnalysis = () => {
		if (activeProviderTab === null) {
			return;
		}

		if (canChooseTeamTagging && isTeamTaggingValid === false) {
			addErrorToast("Select at least one team for Sportlogiq tagging");
			return;
		}

		if (!confirm(`Are you sure to request analysis from "${activeProviderTab}"?`)) {
			return;
		}
		setHasProgress(true);
		createGameAnalysisMutation.mutate({
			gameId: gameData.id,
			body: {
				provider: activeProviderTab,
				priority: priority,
				processingLevel: isSportlogiqRequest ? processingLevel : undefined,
				teamTagging: getTeamTagging(),
				requestSportlogiqWithPhysical:
					activeProviderTab === DiscoverGameAnalysisProvider.Sportlogiq ? requestSportlogiqWithPhysical : false,
			}
		})
	}

	const handleRequestSportlogiqWithPhysicalChange = (isChecked: boolean) => {
		if (isChecked && hasTrustedVeoLink === false) {
			addErrorToast(_("gameAnalysis.veoLink.requiredGameUploads"));
			setRequestSportlogiqWithPhysical(false);
			return;
		}
		setRequestSportlogiqWithPhysical(isChecked);
	};

	return (
		<div className="game-analysis-dialog">
			{canRequestAnalysis}
			<Tabs items={tabs} active={activeProviderTab} onClick={(key) => setActiveProviderTab(key)} />

			{/* analysis in progress */}
			{visibleProviderAnalysis && (
				<div>
					<table className="discover-table" style={{ width: "100%" }}>
						<tbody>
							<tr style={{ width: "180px" }}>
								<th>Status</th>
								<td>{visibleProviderAnalysis.status || "-"} {visibleProviderAnalysis.statusDetailed && <div style={{ color: "red" }}>{visibleProviderAnalysis.statusDetailed}</div>}</td>
							</tr>
							<tr>
								<th>Priority</th>
								<td>{visibleProviderAnalysis.priority || "-"}</td>
							</tr>
							<tr>
								<th>Processing Level</th>
								<td>{visibleProviderAnalysis.processingLevel}</td>
							</tr>
							<tr>
								<th>Created</th>
								<td>
									{formatDateTime(visibleProviderAnalysis.createdAt) || "-"}
									{visibleProviderAnalysis.createdBy && <Link to={`/discover/user/${visibleProviderAnalysis.createdBy.id}`} target="_blank">
										<br /><strong style={{ fontSize: '.8em' }}>{visibleProviderAnalysis.createdBy.name}</strong>
									</Link>}
								</td>
							</tr>
							<tr>
								<th>Requested</th>
								<td>
									{formatDateTime(visibleProviderAnalysis.requestedAt) || "-"}
									{visibleProviderAnalysis.requestedBy && <Link to={`/discover/user/${visibleProviderAnalysis.requestedBy.id}`} target="_blank">
										<br /><strong style={{ fontSize: '.8em' }}>{visibleProviderAnalysis.requestedBy.name}</strong>
									</Link>}
								</td>
							</tr>
							{visibleProviderAnalysis.finalAt && <tr>
								<th>Final At</th>
								<td>{formatDateTime(visibleProviderAnalysis.finalAt)}</td>
							</tr>}
							{visibleProviderAnalysis.firstAvailableAt && <tr>
								<th>First Available At</th>
								<td>{formatDateTime(visibleProviderAnalysis.firstAvailableAt)}</td>
							</tr>}
							{visibleProviderAnalysis.analyzedAt && <tr>
								<th>Analyzed At</th>
								<td>{formatDateTime(visibleProviderAnalysis.analyzedAt)}</td>
							</tr>}
							<tr>
								<th>ID</th>
								<td>{visibleProviderAnalysis.id}</td>
							</tr>
						</tbody>
					</table>
					{visibleProviderAnalysis.isInProgress && <span className="button" aria-disabled={hasProgress} onClick={() => handleRejectGameAnalysis(visibleProviderAnalysis.id)}>Reject</span>}
				</div>
			)}

			{/* create analysis dialog */}
			{!actualAnalysis && !visibleProviderAnalysis && activeProviderTab && canRequestAnalysis && (
				<div>
					<div className="game-analysis-dialog__columns">
						<div>
							<div className="game-analysis-dialog__label">Priority:</div>
							<input type="number" min={1} max={AnalysisType.MAX} placeholder="Priority" value={priority} onChange={(e) => setPriority(Number(e.currentTarget.value))} />
						</div>
						<div>
							{activeProviderTab === DiscoverGameAnalysisProvider.Sportlogiq && <div>
								<div className="game-analysis-dialog__label">Processing Level</div>
								<label htmlFor="processing-level-full">
									<input type="radio" id="processing-level-full" name="processing-level" checked={processingLevel === "full"} onChange={() => setProcessingLevel("full")} />
									Full
								</label>
								<label htmlFor="processing-level-limited">
									<input type="radio" id="processing-level-limited" name="processing-level" checked={processingLevel === "limited"} onChange={() => setProcessingLevel("limited")} />
									Limited
								</label>
								{canChooseTeamTagging && <div>
									<div className="game-analysis-dialog__label">Team Tagging</div>
									<label htmlFor="team-tagging-home">
										<input
											type="checkbox"
											id="team-tagging-home"
											name="team-tagging-home"
											checked={isHomeTeamTagged}
											onChange={(e) => setIsHomeTeamTagged(e.currentTarget.checked)}
										/>
										Home
									</label>
									<label htmlFor="team-tagging-away">
										<input
											type="checkbox"
											id="team-tagging-away"
											name="team-tagging-away"
											checked={isAwayTeamTagged}
											onChange={(e) => setIsAwayTeamTagged(e.currentTarget.checked)}
										/>
										Away
									</label>
								</div>}
								<div className="physical-analysis-checkbox">
									<input
										type="checkbox"
										id="request-sportlogiq-physical"
										checked={requestSportlogiqWithPhysical}
										disabled={hasTrustedVeoLink === false}
										aria-describedby={hasTrustedVeoLink === false ? "request-sportlogiq-physical-veo-notice" : undefined}
										onChange={(event) => handleRequestSportlogiqWithPhysicalChange(event.target.checked)}
									/>
									{" "}{_any("label.sportlogiq_physical")}
								</div>
								{hasTrustedVeoLink === false && (
									<div
										id="request-sportlogiq-physical-veo-notice"
										className="game-analysis-dialog__veo-notice"
										role="status"
									>
										{_any("gameAnalysis.veoLink.missingGameUploadsHint", { gameId: String(gameData.id) })}{" "}
										<Link to="/admin/videos" target="_blank" rel="noopener noreferrer">
											{_any("gameAnalysis.veoLink.gameUploadsLinkLabel")}
										</Link>
									</div>
								)}
							</div>}
						</div>
					</div>
					<span className="button" aria-disabled={hasProgress || (canChooseTeamTagging && isTeamTaggingValid === false)} onClick={() => handleCreateGameAnalysis()}>Request Analysis from {activeProviderTab}</span>
				</div>
			)}

			{/* not possible to create analysis */}
			{!visibleProviderAnalysis && !canRequestAnalysis && activeProviderTab && (
				<div style={{ background: "#fec", padding: "20px", borderRadius: "5px", textAlign: "center" }}>
					Analysis from "{activeProviderTab}" cannot be requested now.
				</div>
			)}
		</div>
	)
}
